export const inputCls =
  'bg-white border-slate-200 focus:border-indigo-500 focus:ring-indigo-500/20 text-slate-900 placeholder:text-slate-400';
export const labelCls = 'text-slate-700 font-medium text-sm';
export const sectionTitle = 'text-xl font-semibold text-slate-900 tracking-tight';
export const sectionSub = 'text-sm text-slate-600';
export const cardCls =
  'rounded-2xl border border-slate-200/90 bg-white shadow-sm overflow-hidden';
export const cardHeaderCls =
  'px-6 py-5 border-b border-slate-100 bg-gradient-to-br from-slate-50/90 to-white';

export const destructiveSoft =
  'border border-red-200 bg-white text-red-700 shadow-sm hover:bg-red-50 hover:border-red-300 hover:text-red-800';
export const destructiveEmphasis =
  'border border-red-300 bg-red-50/90 text-red-800 shadow-sm hover:bg-red-100 hover:border-red-400 hover:text-red-900';
export const dialogCancelClass =
  'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:text-slate-900';
export const dialogDestructiveConfirmClass =
  'border border-rose-600 bg-rose-600 text-white hover:bg-rose-700 hover:border-rose-700 shadow-sm';

import supabase from '../supabaseClient';
import { TABLE_NAMES } from '../config';
import { migrateCommunityIsPrivate } from './communityPrivacy';

export const prefsStorageKey = (communityId) =>
  communityId ? `statbox:community:prefs:${communityId}` : null;

// Builds the pre-written WhatsApp join message for a given community.
// We bake the community name into the text when we seed so managers see a
// finished, human sentence instead of a Mustache-looking placeholder.
export function buildDefaultWhatsappJoinMessage(communityName) {
  const trimmed = (communityName || '').trim();
  return trimmed
    ? `Hi! I'd like to join your community "${trimmed}".`
    : `Hi! I'd like to join your community.`;
}

// Old defaults we may find persisted in communities.game_defaults. Treat them
// as "still the default" so we can safely overwrite them with the new seed
// during a one-time migration on first load.
export const LEGACY_DEFAULT_WHATSAPP_JOIN_MESSAGES = [
  "Hi! I'd like to join the game on {{date}}.",
];

export const defaultPrefs = {
  defaultGameType: 'basic',
  defaultVisibility: 'community',
  defaultJoinBehavior: 'auto',
  discoveryEnabled: true,
  discoveryBudgetUsd: '200',
  defaultJoinMethod: 'request',
  defaultJoinMethodWhatsappNumber: '',
  defaultJoinMethodWhatsappMessage: '',
  whatsappGroupLink: '',
  // Privacy / management diagnostic — pre-filled from the wizard's
  // policies step in setup mode, then editable on the portal config
  // page. Empty string defaults so existing communities silently
  // inherit the WhatsApp-card default UI until they actively switch.
  visibility: 'public',          // 'public' | 'private'
  managementType: '',            // '' | 'whatsapp' | 'app' — '' renders WhatsApp card
  currentPlatform: '',           // platform key from MANAGEMENT_PLATFORMS
  currentPlatformOther: '',      // freeform when currentPlatform === 'other'
  platformHandle: '',            // optional URL or handle into the platform
  integrationsInterest: true,    // checkbox; defaults to opt-in
  // Legacy game-defaults field; mobile join flow may still read this.
  communityJoinPolicy: 'request', // 'open' | 'request' | 'invite'
};

// Keep only keys we know about so unrelated client state doesn't leak to the DB.
function sanitizePrefs(prefs) {
  const out = {};
  for (const key of Object.keys(defaultPrefs)) {
    if (prefs && Object.prototype.hasOwnProperty.call(prefs, key)) {
      out[key] = prefs[key];
    }
  }
  return out;
}

/** Map signup wizard policies → communities.game_defaults shape. */
export function gameDefaultsFromWizardPolicies(policies = {}, communityName = '') {
  const managementType =
    policies.managementType === 'whatsapp' || policies.managementType === 'app'
      ? policies.managementType
      : '';
  return sanitizePrefs({
    ...defaultPrefs,
    defaultJoinMethodWhatsappNumber: policies.whatsappNumber || '',
    whatsappGroupLink: policies.whatsappGroupLink || '',
    defaultJoinMethodWhatsappMessage: buildDefaultWhatsappJoinMessage(communityName),
    managementType,
    currentPlatform: policies.currentPlatform || '',
    currentPlatformOther: policies.currentPlatformOther || '',
    platformHandle: policies.platformHandle || '',
    integrationsInterest: policies.integrationsInterest !== false,
    visibility: migrateCommunityIsPrivate(policies) ? 'private' : 'public',
  });
}

export function loadPrefs(communityId) {
  const key = prefsStorageKey(communityId);
  if (!key) return { ...defaultPrefs };
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return { ...defaultPrefs };
    return { ...defaultPrefs, ...JSON.parse(raw) };
  } catch {
    return { ...defaultPrefs };
  }
}

// Writes immediately to the localStorage cache so subsequent reads are instant.
// Remote persistence happens via persistPrefsToDb (debounced).
export function savePrefs(communityId, prefs) {
  const key = prefsStorageKey(communityId);
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify(prefs));
  } catch (_) {}
  persistPrefsToDb(communityId, prefs);
}

// Source of truth: communities.game_defaults (JSONB). Falls back to the
// localStorage cache on any error so the UI keeps working offline.
export async function fetchPrefsFromDb(communityId) {
  if (!communityId) return { ...defaultPrefs };
  try {
    const { data, error } = await supabase
      .from(TABLE_NAMES.COMMUNITIES)
      .select('game_defaults')
      .eq('id', communityId)
      .single();
    if (error) throw error;
    const remote = data?.game_defaults && typeof data.game_defaults === 'object'
      ? data.game_defaults
      : {};
    const merged = { ...defaultPrefs, ...remote };
    // Refresh the localStorage cache so next mount paints the same values.
    const key = prefsStorageKey(communityId);
    if (key) {
      try { localStorage.setItem(key, JSON.stringify(merged)); } catch (_) {}
    }
    return merged;
  } catch (err) {
    // Intentionally fall back to cache on any failure (missing column, RLS, offline, …).
    return loadPrefs(communityId);
  }
}

// Debounce DB writes per-community so rapid keystrokes don't hammer Supabase.
const pendingWrites = new Map(); // communityId -> { timer, prefs }

async function flushPrefsWrite(communityId) {
  const entry = pendingWrites.get(communityId);
  if (!entry) return;
  pendingWrites.delete(communityId);
  try {
    await supabase
      .from(TABLE_NAMES.COMMUNITIES)
      .update({ game_defaults: sanitizePrefs(entry.prefs) })
      .eq('id', communityId);
  } catch (err) {
    // Silent: caller already has optimistic localStorage cache; surface via console for debugging.
    console.error('Failed to persist community game defaults', err);
  }
}

export function persistPrefsToDb(communityId, prefs, { delayMs = 400 } = {}) {
  if (!communityId) return;
  const existing = pendingWrites.get(communityId);
  if (existing?.timer) clearTimeout(existing.timer);
  const timer = setTimeout(() => flushPrefsWrite(communityId), delayMs);
  pendingWrites.set(communityId, { timer, prefs });
}

// Best-effort flush, e.g. on unmount or before navigation.
export async function flushPrefsWrites(communityId) {
  const entry = pendingWrites.get(communityId);
  if (!entry) return;
  clearTimeout(entry.timer);
  await flushPrefsWrite(communityId);
}
